'use client'

import React, { useState } from 'react'
import { clsx } from 'clsx'
import { Icon } from '@/components/Icon'
import Image from '@/components/Image/Image'
import { Link } from '@/components/Link'
import { Typography } from '@/components/Typography'
import { useIsPurMode } from '@/hooks/useIsPurMode'
import { Teaser as TeaserTypes, WidgetTeaserProps, MetaData } from '@/types/cms'
import { getLinkPathname } from '@/utils/urls'
import styles from './Teaser.module.scss'
import AppContext from '@/types/context'
import { getAppConfig } from '@/config/config'

const millisToMinutesAndSeconds = (millis: number) => {
  const minutes = Math.floor(millis / 60000)
  const seconds = Math.round((millis % 60000) / 1000)

  if (seconds === 60) return '01:00'

  const paddedSeconds = seconds < 10 ? `0${seconds}` : seconds
  const result = `${minutes}:${paddedSeconds} min`

  return minutes < 10 ? `0${result}` : result
}

const Teaser = ({
  teaser,
  item,
  context,
  className,
  isInline,
  teaserImgLeft,
  teaserInlineTop,
  as: DynamicTag = 'article',
  isFirstBig,
  cropOverride,
}: {
  teaser: TeaserTypes
  context: AppContext
  item: WidgetTeaserProps
  className?: string
  isInline?: boolean
  teaserImgLeft?: boolean
  teaserInlineTop?: boolean
  as?: keyof React.JSX.IntrinsicElements
  isFirstBig?: boolean
  cropOverride?: string
}) => {
  const config = getAppConfig()

  const model = item.model

  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [galleryData, setGalleryData] = useState<null | {
    id: string
    items: Array<{
      model: {
        id?: string
        fields: { credits: string; caption: string }
        title: string
        image: {
          filename: string
          baseUrl: string
          crop: string
          heightRatio: number
        }
        imageAttributes: {
          title?: string
          credit?: string
        }
      }
    }>
  }>(null)
  const [galleryMetaData, setGalleryMetaData] = useState<undefined | MetaData>(undefined)

  const isPurMode = useIsPurMode(context.cookies)

  const image = teaser.model.image
    ? {
        ...teaser.model.image,
        heightRatio: teaser.model.image.heightRatio ?? 1,
        crop: isFirstBig ? 'Img_17_6' : (cropOverride ?? teaser.model.image.crop ?? 'Img_16_9'),
      }
    : undefined

  const imageFileName = image?.filename ?? 'image'
  const dateObject = new Date(teaser.model.publishedAt)

  const handleGalleryClick = async (e: React.MouseEvent) => {
    e.preventDefault()

    if (!teaser.model['fragment-url']) {
      console.error('No fragment URL provided for gallery')
      return
    }

    try {
      setIsLoading(true)
      const response = await fetch('/nextapi/getdata?url=' + encodeURIComponent(teaser.model['fragment-url']))
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      setGalleryData(data.areas?.main?.[0])
      setGalleryMetaData(data.areas?.meta?.[0])
      setIsPopupOpen(true)
    } catch (err) {
      console.error('Error fetching gallery data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const galleryContext = galleryData
    ? {
        ...galleryData,
        items: galleryData.items.map((item) => ({
          model: {
            ...item.model,
            id: item.model.id ?? item.model.title ?? Math.random().toString(36).slice(2),
            fields: {
              caption: item.model.fields?.caption ?? '',
              credits: item.model.fields?.credits ?? '',
            },
            imageAttributes: {
              title: item.model.imageAttributes?.title ?? '',
              credit: item.model.imageAttributes?.credit ?? '',
            },
          },
        })),
      }
    : {
        id: teaser.id,
        items: [
          {
            model: {
              id: teaser.id,
              title: teaser.model.headline,
              image: image || {
                filename: imageFileName,
                baseUrl: '',
                crop: 'Img_16_9',
                heightRatio: 1,
              },
              imageAttributes: {
                title: teaser.model.imageAttributes?.title ?? '',
                credit: teaser.model.imageAttributes?.credit ?? '',
              },
            },
          },
        ],
      }

  const TitleTag = model?.pageType === 'article' && model?.pageContentType !== 'author' ? 'div' : 'h2'

  const renderDate = () => {
    const now = new Date()
    const isToday =
      dateObject.getDate() === now.getDate() &&
      dateObject.getMonth() === now.getMonth() &&
      dateObject.getFullYear() === now.getFullYear()

    return isToday
      ? `${dateObject.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
        })} Uhr`
      : dateObject.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
  }

  const renderImageInfo = () => {
    if (teaser.model.type === 'video') {
      return (
        <div className={styles['image-info']}>
          <Icon name="play" size={32} />
          <Typography as="span" variant="xs">
            {teaser.model.mode === 'vod' && teaser.model.durationMillis
              ? millisToMinutesAndSeconds(teaser.model.durationMillis)
              : 'LIVE'}
          </Typography>
        </div>
      )
    }

    if (teaser.model.type === 'gallery') {
      return (
        <div className={styles['image-info']}>
          <Icon name="gallery" size={32} />
          <Typography as="span" variant="xs">
            {teaser.model.galImgCount} Bilder
          </Typography>
        </div>
      )
    }

    return null
  }

  const content = (
    <>
      <div className={clsx(styles.image, 'image')}>
        <Image image={image} alt={imageFileName} fallbackImage={`${config.basePath}/assets/logos/logo_grey.svg`} />
        {renderImageInfo()}
      </div>

      {!teaserInlineTop && (
        <div className={styles.wrapper}>
          {model?.fields?.showDate && model?.fields?.showTimeBeforeTitle && (
            <Typography as="span" variant="s" color="darkgrey" className={styles['date-top']}>
              {renderDate()}
            </Typography>
          )}

          <TitleTag>
            <Typography as="span" variant="s" weight="bold" color="red">
              {teaser.model.kicker}
            </Typography>
            <Typography as="span" variant={isInline ? 'm' : 'xxxl'} weight="bold" className={styles.title}>
              {teaser.model.headline}
            </Typography>
          </TitleTag>

          {!teaser.model.ad && model?.fields?.showLeadtext && teaser.model.leadtext && (
            <Typography as="p" className={styles.leadtext}>
              {teaser.model.leadtext}
            </Typography>
          )}

          {(teaser.model.ad ||
            teaser.model.updated ||
            model?.fields?.hideRubric ||
            model?.fields?.showDate ||
            teaser.model.author) && (
            <Typography as="div" variant="xs" color="darkgrey" className={styles.info}>
              {teaser.model.ad && <div className={styles.ad}>Anzeige</div>}

              {teaser.model.updated && (
                <Typography as="div" color="red" variant="xs">
                  Updated
                </Typography>
              )}

              {model?.fields?.hideRubric && <span className={styles['type-title']}>{teaser.model.homeSection}</span>}

              {model?.fields?.showDate && !model?.fields?.showTimeBeforeTitle && <span>{renderDate()}</span>}

              {teaser.model.author && <span>{teaser.model.author}</span>}
            </Typography>
          )}
        </div>
      )}
    </>
  )

  const teaserClasses = clsx(styles.teaser, teaserImgLeft && styles['img-left'], className, isLoading && styles.loading)

  return (
    <>
      {!(teaser.model.ad && isPurMode) && (
        <DynamicTag title={teaser.model.headline}>
          <Link
            className={clsx(styles.teaser, teaserImgLeft && styles['img-left'], className)}
            href={teaser.model.linkExternal ? teaser.model.linkExternal.url : getLinkPathname(teaser.model.url)}
            target={teaser.model.linkExternal ? teaser.model.linkExternal.target : undefined}
            title={teaser.model.headline}
          >
            {content}
          </Link>
        </DynamicTag>
      )}
    </>
  )
}

export default Teaser
